import React, { useState, useEffect } from 'react';
import './GreaterChinaLaunch.css';
import { useAdobeDataLayer, PageNames } from '../../hooks/useAdobeDataLayer';
import ReportModal from '../../components/Redesign/ReportModal';
import ContactBanner from '../../components/Redesign/ContactBanner';
import OptimizedImage from '../../components/Redesign/OptimizedImage';

import buildYourMomentImg from 'url:../../assets-optimized/redesign/pressRelease/build_your_moment_press_release.jpg';
import buildYourMomentImgAvif from 'url:../../assets-optimized/redesign/pressRelease/build_your_moment_press_release.avif';
import buildYourMomentImgWebp from 'url:../../assets-optimized/redesign/pressRelease/build_your_moment_press_release.webp';

import heroImg from 'url:../../assets-optimized/redesign/pressRelease/greater_china_press_hero_image.jpg';
import heroImgAvif from 'url:../../assets-optimized/redesign/pressRelease/greater_china_press_hero_image.avif';
import heroImgWebp from 'url:../../assets-optimized/redesign/pressRelease/greater_china_press_hero_image.webp';

import sideImg from 'url:../../assets-optimized/redesign/pressRelease/woman_reading_press_release.jpg';
import sideImgAvif from 'url:../../assets-optimized/redesign/pressRelease/woman_reading_press_release.avif';
import sideImgWebp from 'url:../../assets-optimized/redesign/pressRelease/woman_reading_press_release.webp';

const DownloadIcon = () => (
  <svg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M10 33.3307H30M20 6.66406V26.6641M14.1667 20.8307L20 26.6641L25.8333 20.8307'
      stroke='#171717'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const GreaterChinaLaunch = () => {
  const { sendPageView } = useAdobeDataLayer();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    sendPageView(PageNames.GreaterChinaLaunch);
  }, [sendPageView]);

  return (
    <div className='press-release-page'>
      <div className='press-release-container'>
        {/* Full-width intro — spans both columns */}
        <span className='pr-category-badge'>News</span>

        <h1 className='pr-headline'>
          Marriott International Introduces Marriott Media to Greater China
        </h1>

        <p className='pr-date'>June 22, 2026, Shanghai, China</p>

        <div className='pr-hero-image'>
          <OptimizedImage
            avif={heroImgAvif}
            webp={heroImgWebp}
            fallback={heroImg}
            alt='Marriott Media Greater China launch'
          />
        </div>

        {/* Left column: all article body content */}
        <div className='pr-article-body'>
          <p className='pr-body-text'>
            As the industry’s first hospitality media network, Marriott Media leverages the globally
            recognized Marriott Bonvoy program of over 283 million members, and with this launch, an
            extensive portfolio of more than 700 hotels in Greater China. Marriott Media features a
            diverse array of touchpoints spanning the entire travel journey, enabling brand partners
            to engage new audiences traveling in the region meaningfully with more than 500
            targetable attributes, without disrupting the travel experience.
          </p>

          <div className='pr-section-image'>
            <OptimizedImage
              avif={sideImgAvif}
              webp={sideImgWebp}
              fallback={sideImg}
              alt='Shanghai, Greater China'
            />
          </div>

          <p className='pr-large-statement'>
            By seamlessly integrating brands throughout every stage of their travel journey,
            Marriott Media helps deliver diverse, immersive and inspiring travel experiences.
          </p>

          <blockquote className='pr-pull-quote'>
            <p>
              <span className='pr-pull-quote__bracket'>“</span>Greater China marks Marriott Media’s
              second global launch following North America and a pivotal step in scaling a new kind
              of personalization platform rooted in the power of travel,
              <span className='pr-pull-quote__bracket'>”</span>
            </p>
            <footer>
              <strong>GARFIELD YANG,</strong>
              <br />
              VICE PRESIDENT AND HEAD OF Marriott Media, GREATER CHINA
            </footer>
          </blockquote>

          <p className='pr-body-text'>
            “By seamlessly connecting brand storytelling with real-world experiences, we enable
            partners to build deeper brand mindshare through immersive travel experiences, drive
            efficient interest-to-experience conversion within authentic travel moments, and
            cultivate long-term brand equity through meaningful engagement with high-value
            audiences.”
          </p>
          <p className='pr-body-text'>
            The launch of Marriott Media aligns with immense potential and transformative trends
            shaping today’s travel market. According to a joint research report released Marriott
            Media and United Airline’s Kinective Media, travel environments inherently offer unique
            advantages of high attention, high trust, and rich experiential value. 
          </p>

          <div className='pr-download-row'>
            <div className='pr-download-info'>
              <span className='pr-download-label'>
                Joint Research by Marriott Media &amp; Kinective Media
              </span>
              <span className='pr-download-title'>DOWNLOAD THE PSYCHOLOGY OF TRAVEL REPORT</span>
            </div>
            <button
              className='pr-download-btn'
              onClick={() => setIsPopupOpen(true)}
              aria-label='Download report'
            >
              <DownloadIcon />
            </button>
          </div>

          <p className='pr-body-text'>
            Travelers are more open, excited and context-aware of brand messages than in everyday
            settings, while frequent travelers demonstrate particularly strong levels of brand trust
            and enthusiasm. Brands should capitalize on these evolving traveler needs and
            psychological shifts by meeting guest expectations, enhancing the travel experience, and
            strengthening marketing effectiveness to forge meaningful, lasting connections.
          </p>
        </div>

        {/* Right column: sidebar spanning full article height */}
        <div className='pr-sidebar-box'>
          <h2 className='pr-side-heading'>
            RESHAPING BRAND-GUEST ENGAGEMENT THROUGHOUT THE TRAVEL JOURNEY
          </h2>
          <p className='pr-body-text'>
            Marriott International today announced the launch of Marriott Media in Greater China at
            the 2026 Cannes Lions International Festival of Creativity.
          </p>
        </div>
      </div>

      <ContactBanner
        title="LET’S BUILD YOUR MOMENT"
        bgImage={buildYourMomentImg}
        bgImageAvif={buildYourMomentImgAvif}
        bgImageWebp={buildYourMomentImgWebp}
        ctaStyle="black"
      />

      <ReportModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default GreaterChinaLaunch;
